import {
  collection,
  query,
  where,
  limit,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../fb";
import { checkIsValidAddress } from "@/store/utils/ordersUtils";
import { dbCollection, orderFilterCategories, orderStatus } from "@/constants";
import { appSettings } from "@/config";

const defaultState = {
  loadingOrders: false,
  orders: [],
  ordersToFulfill: [],
  /** Erst-Snapshot der Versandliste komplett verarbeitet (inkl. Tasks) */
  ordersToFulfillReady: false,
  ordersInFulfill: [],
  ordersWithError: [],
  filter: {
    orderId: "",
    email: "",
    quickSearch: "",
    filterCategory: orderFilterCategories.ALL,
  },
  unsubOrdersToFulfill: () => {},
  unsubOrdersInFulfill: () => {},
  unsubOrdersWithError: () => {},
};

const getters = {
  ordersToFulfill: (state) => state.ordersToFulfill,
  ordersToFulfillReady: (state) => state.ordersToFulfillReady,
  ordersInFulfill: (state) => state.ordersInFulfill,
  ordersWithError: (state) => state.ordersWithError,
  loadingOrders: (state) => state.loadingOrders,
  ordersPerPage: (state) => state.ordersPerPage,
  currentOrdersPage: (state) => state.currentOrdersPage,
  ordersSortBy: (state) => state.filter.sortBy,
  ordersSortDesc: (state) => state.filter.sortDesc,
  filterOrderId: (state) => state.filter.orderId,
  filterEmail: (state) => state.filter.email,
  filterQuickSearch: (state) => state.filter.quickSearch,
  filterCategory: (state) => state.filter.filterCategory,
  orders: (state) => state.orders,
};

const actions = {
  async loadOrders({ dispatch }) {
    dispatch("loadOrdersToFulfill");
    dispatch("loadOrdersInFulfill");
    dispatch("loadOrdersWithError");
  },
  async loadAllOrders({ commit, state }) {
    commit("setLoadingOrders", true);
    commit("setOrders", {
      orders: [],
      stateFieldName: "orders",
    });

    const ordersCollection = collection(db, "orders");
    let ordersQuery = query(
      ordersCollection,
      orderBy("orderDate", "desc"),
      limit(50)
    );

    const { orderId, email, filterCategory } = state.filter;

    if (filterCategory === orderFilterCategories.DELAYED_FULFILL) {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(
        threeDaysAgo.getDate() - appSettings.MAX_DAYS_UNTIL_FULFILL
      );

      ordersQuery = query(
        ordersCollection,
        where("orderDate", "<", threeDaysAgo.toISOString()),
        where("status", "in", [
          orderStatus.IN_PROGRESS,
          orderStatus.ORDER_CREATED,
        ]),
        limit(100)
      );
    }

    if (filterCategory === orderFilterCategories.DELAYED_SHIPMENT) {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(
        threeDaysAgo.getDate() - appSettings.MAX_DAYS_UNTIL_SHIPMENT
      );

      ordersQuery = query(
        ordersCollection,
        where("fulfillDate", "<", threeDaysAgo.toISOString()),
        where("status", "==", orderStatus.FULFILLED),
        orderBy("fulfillDate", "desc"),
        limit(500)
      );
    }

    if (orderId) {
      ordersQuery = query(
        ordersCollection,
        orderBy("orderName", "desc"),
        where("orderName", ">=", orderId),
        where("orderName", "<=", orderId + "~"),
        limit(50)
      );
    }

    if (email) {
      ordersQuery = query(ordersQuery, where("customer.email", "==", email));
    }

    const querySnapshot = await getDocs(ordersQuery);
    const orders = querySnapshot.docs.map((doc) => doc.data());

    commit("setOrders", {
      orders: orders,
      stateFieldName: "orders",
    });
    commit("setLoadingOrders", false);
  },
  async loadOrdersToFulfill({ commit, state }) {
    if (state.unsubOrdersToFulfill && state.unsubOrdersToFulfill.__active) {
      return;
    }

    commit("setLoadingOrders", true);
    commit("setOrders", {
      orders: [],
      stateFieldName: "ordersToFulfill",
    });

    const ordersCollection = collection(db, "orders");
    const ordersQuery = query(
      ordersCollection,
      where("status", "==", orderStatus.READY_FOR_FULFILL),
      orderBy("orderDate", "desc"),
      limit(600)
    );

    if (typeof state.unsubOrdersToFulfill === "function") {
      state.unsubOrdersToFulfill();
    }
    commit("setOrdersToFulfillReady", false);
    let firstSnapshot = true;
    const unsub = onSnapshot(ordersQuery, async (snapshot) => {
      const isInitial = firstSnapshot;
      firstSnapshot = false;
      // Jede Änderung lädt die Tasks der Bestellung nach — beim Erst-Snapshot alle abwarten,
      // damit die Liste erst dann als „geladen" gilt (OrdersToFulfill puffert danach Neuzugänge).
      await Promise.all(
        snapshot.docChanges().map(async (change) => {
          const changedOrder = change.doc.data();
          const showOrderWarning = !checkIsValidAddress(changedOrder);
          const fullTasks = [];
          for await (const orderTask of changedOrder.tasks) {
            const taskDoc = doc(db, dbCollection.TASKS, orderTask.taskUid);
            const taskRes = await getDoc(taskDoc);
            if (taskRes.exists()) {
              fullTasks.push({
                ...orderTask,
                ...taskRes.data(),
              });
            }
          }
          const orderData = {
            ...changedOrder,
            showOrderWarning,
            tasks: fullTasks,
          };
          if (change.type === "added") {
            commit("addOrder", {
              order: orderData,
              stateFieldName: "ordersToFulfill",
            });
          } else if (change.type === "modified") {
            commit("updateOrder", {
              order: orderData,
              stateFieldName: "ordersToFulfill",
            });
          } else if (change.type === "removed") {
            commit("deleteOrder", {
              order: orderData,
              stateFieldName: "ordersToFulfill",
            });
          }
        })
      );
      if (isInitial) {
        commit("setOrdersToFulfillReady", true);
        commit("setLoadingOrders", false);
      }
    });
    unsub.__active = true;
    state.unsubOrdersToFulfill = unsub;
  },
  async loadOrdersInFulfill({ commit, state }) {
    if (state.unsubOrdersInFulfill && state.unsubOrdersInFulfill.__active) {
      return;
    }

    commit("setLoadingOrders", true);
    commit("setOrders", {
      orders: [],
      stateFieldName: "ordersInFulfill",
    });

    const ordersCollection = collection(db, "orders");
    const ordersQuery = query(
      ordersCollection,
      where("status", "in", [
        orderStatus.WAITING_FOR_FULFILL,
        orderStatus.IN_FULFILL,
      ]),
      orderBy("orderDate", "desc"),
      limit(250)
    );

    if (typeof state.unsubOrdersInFulfill === "function") {
      state.unsubOrdersInFulfill();
    }
    const unsub = onSnapshot(ordersQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const changedOrder = change.doc.data();
        const showOrderWarning = !checkIsValidAddress(changedOrder);
        const orderData = {
          ...changedOrder,
          showOrderWarning,
        };
        if (change.type === "added") {
          commit("addOrder", {
            order: orderData,
            stateFieldName: "ordersInFulfill",
          });
        } else if (change.type === "modified") {
          commit("updateOrder", {
            order: orderData,
            stateFieldName: "ordersInFulfill",
          });
        } else if (change.type === "removed") {
          commit("deleteOrder", {
            order: orderData,
            stateFieldName: "ordersInFulfill",
          });
        }
      });
    });
    unsub.__active = true;
    state.unsubOrdersInFulfill = unsub;

    commit("setLoadingOrders", false);
  },
  async loadOrdersWithError({ commit, state }) {
    if (state.unsubOrdersWithError && state.unsubOrdersWithError.__active) {
      return;
    }

    commit("setLoadingOrders", true);
    commit("setOrders", {
      orders: [],
      stateFieldName: "ordersWithError",
    });

    const ordersCollection = collection(db, "orders");
    const ordersQuery = query(
      ordersCollection,
      where("status", "==", orderStatus.FULFILL_ERROR),
      orderBy("orderDate", "desc"),
      limit(100)
    );

    if (typeof state.unsubOrdersWithError === "function") {
      state.unsubOrdersWithError();
    }
    const unsub = onSnapshot(ordersQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const changedOrder = change.doc.data();
        const showOrderWarning = !checkIsValidAddress(changedOrder);
        const orderData = {
          ...changedOrder,
          showOrderWarning,
        };
        if (change.type === "added") {
          commit("addOrder", {
            order: orderData,
            stateFieldName: "ordersWithError",
          });
        } else if (change.type === "modified") {
          commit("updateOrder", {
            order: orderData,
            stateFieldName: "ordersWithError",
          });
        } else if (change.type === "removed") {
          commit("deleteOrder", {
            order: orderData,
            stateFieldName: "ordersWithError",
          });
        }
      });
    });
    unsub.__active = true;
    state.unsubOrdersWithError = unsub;

    commit("setLoadingOrders", false);
  },
  unsubscribeOrders({ state }) {
    ["unsubOrdersToFulfill", "unsubOrdersInFulfill", "unsubOrdersWithError"].forEach((key) => {
      if (typeof state[key] === "function") state[key]();
      state[key] = () => {};
    });
  },
};

const mutations = {
  setLoadingOrders: (state, bool) => (state.loadingOrders = bool),
  setOrdersToFulfillReady: (state, bool) => (state.ordersToFulfillReady = bool),
  setOrders: (state, { orders, stateFieldName }) =>
    (state[stateFieldName] = orders),
  addOrder(state, { order, stateFieldName }) {
    // Add the task to the state
    if (order.isExpress) {
      state[stateFieldName].unshift(order);
    } else {
      state[stateFieldName].push(order);
    }
  },
  updateOrder(state, { order, stateFieldName }) {
    // Find the index of the task in the state
    const index = state[stateFieldName].findIndex((o) => o.uid === order.uid);
    // Update the task in the state
    if (index !== -1) {
      state[stateFieldName].splice(index, 1, order);
    }
  },
  deleteOrder(state, { order, stateFieldName }) {
    // Find the index of the task in the state
    const index = state[stateFieldName].findIndex((o) => o.uid === order.uid);
    // Remove the task from the state
    if (index !== -1) {
      state[stateFieldName].splice(index, 1);
    }
  },
  setFilterOrderId: (state, orderId) => (state.filter.orderId = orderId),
  setFilterEmail: (state, email) => (state.filter.email = email),
  setFilterCategory: (state, filterCategory) =>
    (state.filter.filterCategory = filterCategory),
  setFilterQuickSearch: (state, searchTerm) =>
    (state.filter.quickSearch = searchTerm),
};

const state = window.sessionStorage["taskDash"]
  ? JSON.parse(window.sessionStorage["taskDash"]).orders
  : Object.assign({}, defaultState);

export default {
  state,
  getters,
  actions,
  mutations,
};
